import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form'
import { Separator } from '@/components/ui/separator'
import { PersonForm, DocumentForm } from '@/components/forms'
import { DebugPanel } from '@/components/dev/DebugPanel'
import { viatgerSchema } from '@/lib/schemas'
import { type Viatger } from '@/services/viatgers'
import { useUpdateViatger } from '@/hooks/useUpdateViatger'
import { Loader2 } from 'lucide-react'

interface EditViatgerModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  viatger: Viatger
}

export function EditViatgerModal({ open, onOpenChange, onSuccess, viatger }: EditViatgerModalProps) {
  const updateViatgerMutation = useUpdateViatger()

  const form = useForm({
    resolver: zodResolver(viatgerSchema.partial()), // partial() para hacer todos los campos opcionales en edición
    defaultValues: {
      reserva_id: '',
      nom: '',
      cognoms: '',
      tipus_document: 'D' as const,
      num_document: '',
      nacionalitat: 'ES',
      data_naixement: '',
      email: '',
      telefon: '',
      mobil: '',
      adreca: '',
      ciutat: '',
      provincia: '',
      codi_postal: '',
      pais: 'ES',
      peticions_especials: '',
      es_titular: false,
      consentiment_rgpd: false,
      consentiment_marketing: false,
    },
  })

  // Cargar datos del viatger cuando se abre el modal
  useEffect(() => {
    if (open && viatger) {
      form.reset({
        reserva_id: viatger.reserva_id || '',
        nom: viatger.nom || '',
        cognoms: viatger.cognoms || '',
        tipus_document: (viatger.tipus_document as any) || 'DNI',
        num_document: viatger.num_document || '',
        nacionalitat: viatger.nacionalitat || 'ES',
        data_naixement: viatger.data_naixement ? viatger.data_naixement.split('T')[0] : '',
        email: viatger.email || '',
        telefon: viatger.telefon || '',
        mobil: viatger.mobil || '',
        adreca: viatger.adreca || '',
        ciutat: viatger.ciutat || '',
        provincia: viatger.provincia || '',
        codi_postal: viatger.codi_postal || '',
        pais: viatger.pais || 'ES',
        peticions_especials: viatger.peticions_especials || '',
        es_titular: viatger.es_titular || false,
        consentiment_rgpd: viatger.consentiment_rgpd || false,
        consentiment_marketing: viatger.consentiment_marketing || false,
      })
    }
  }, [open, viatger, form])

  const onSubmit = async (data: any) => {
    updateViatgerMutation.mutate(
      { id: viatger.id, data },
      {
        onSuccess: () => {
          onSuccess()
          onOpenChange(false)
        }
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-purple-700">
            Editar Viatger
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Dades Personals */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Dades Personals</h3>
              <PersonForm
                form={form}
                fields={['nom', 'cognoms', 'email', 'telefon', 'mobil', 'data_naixement', 'nacionalitat']}
                required={['nom', 'cognoms']}
              />
            </div>

            <Separator />

            {/* Document d'Identitat */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Document d'Identitat</h3>
              <DocumentForm form={form} required={['num_document']} />
            </div>

            {/* Adreça - COMENTADO: No está bien sincronizado con address_raw, se arreglará más adelante */}
            {/* <div>
              <h3 className="text-lg font-semibold mb-4">Adreça</h3>
              <AddressForm form={form} />
            </div> */}

            {/* <Separator /> */}

            {/* Peticions Especials */}
            <FormField
              control={form.control}
              name="peticions_especials"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Peticions Especials</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={3}
                      placeholder="Al·lèrgies, preferències, etc."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Separator />

            {/* Checkboxes */}
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="es_titular"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>És titular de la reserva</FormLabel>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="consentiment_rgpd"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Consentiment RGPD</FormLabel>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="consentiment_marketing"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Consentiment per enviar comunicacions comercials</FormLabel>
                    </div>
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            {/* Debug Panel - Visible en desarrollo, en producción solo para admin */}
            <DebugPanel
              data={form.watch()}
              title="Debug: Form Data"
              validationErrors={Object.entries(form.formState.errors).map(([key, value]) => ({
                path: [key],
                message: (value as any)?.message || 'Error'
              }))}
              transformedData={viatger}
              showInProd={false}
            />

            {/* Botons */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={updateViatgerMutation.isPending}
              >
                Cancel·lar
              </Button>
              <Button
                type="submit"
                className="bg-purple-600 hover:bg-purple-700"
                disabled={updateViatgerMutation.isPending}
              >
                {updateViatgerMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Actualitzant...
                  </>
                ) : (
                  'Actualitzar Viatger'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
